import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { asyncScheduler, merge, Observable, of } from 'rxjs';
import { filter, mergeMap, observeOn, switchMap, take, tap } from 'rxjs/operators';
import { DomainMessage, IDomainMessage } from '../lemans-app.model';
import { disconnect, retrySignalRConnection, setDomainMessageReceived, updateConnectionState } from './signalr.actions';

import { Action } from '@ngrx/store';
import { HubConnectionError, HubStatus, SignalrService } from '@rockwell-automation-inc/service';
import { LoggerService } from '@servicesV2/logger.service';
import { userLoginComplete } from '../control-page';
import { HubConnectionState } from './hub-connection-state';

@Injectable({
  providedIn: 'root',
})
export class SignalREffects {
  private readonly actions$ = inject(Actions);
  private readonly signalRService = inject(SignalrService);
  private readonly logger = inject(LoggerService).withContext('SignalREffects');
  private readonly router = inject(Router);

  onLoadStartConnection$ = createEffect((): Observable<Action> => {
    // send an initial Connecting state to let the page load continue - otherwise when signalR is down, the page load blocks
    // test with notification service down and refresh dashboard to see the difference
    return this.actions$.pipe(
      ofType(userLoginComplete),
      switchMap(() => [updateConnectionState({ payload: HubConnectionState.Connecting })]),
    );
  });

  setStartConnection$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(userLoginComplete),
      // Defer the actual hub connection until the first route has fully rendered.
      // This keeps the Azure SignalR negotiate call out of the page-load critical
      // path so it does not chain off main.js and inflate LCP.
      // If the router has already completed its initial navigation (e.g. Auth0
      // redirected to /dashboard before userLoginComplete was dispatched), emit
      // immediately rather than waiting for a NavigationEnd that may never come.
      switchMap(() =>
        this.router.navigated
          ? of(null)
          : this.router.events.pipe(
              filter((e) => e instanceof NavigationEnd),
              take(1),
            ),
      ),
      tap(() => {
        this.signalRService.startConnection();
      }),
      switchMap(() =>
        merge(this.signalRService.connectionStatus$, this.signalRService.onDomainMessage$).pipe(
          // SignalR Promise callbacks escape zone.js 0.15 tracking. observeOn(asyncScheduler)
          // re-delivers each emission via setTimeout, which zone.js always patches, ensuring
          // onMicrotaskEmpty fires and ApplicationRef._tick() runs to flush dirty views.
          observeOn(asyncScheduler),
        ),
      ),
      mergeMap((msg: HubStatus | IDomainMessage) => {
        const domainMessage = DomainMessage.create(msg);
        if (domainMessage) {
          return [setDomainMessageReceived({ payload: domainMessage })];
        }
        if (msg instanceof HubConnectionError && msg.isNonRecoverable) {
          return [updateConnectionState({ payload: HubConnectionState.Disconnected, error: msg })];
        }
        if (msg instanceof HubConnectionError) {
          return [updateConnectionState({ payload: HubConnectionState.Reconnecting, error: msg })];
        }
        const hubConnState = msg as unknown as HubConnectionState;
        return [updateConnectionState({ payload: hubConnState })];
      }),
    );
  });

  retrySignalRConnection$ = createEffect(
    (): Observable<Action> => {
      return this.actions$.pipe(
        ofType(retrySignalRConnection),
        tap(() => {
          this.signalRService.startConnection();
        }),
      );
    },
    { dispatch: false },
  );

  disconnect$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(disconnect),
        tap(() => {
          this.logger.log('closing connection');
          this.signalRService.stopConnection();
        }),
      );
    },
    { dispatch: false },
  );

  constructor() {}
}
